// Customer authentication context.
//
// Separate from any existing admin auth — uses its own localStorage key
// (`elite_customer_token`) and its own /api/customer/auth/* endpoints.
//
// Exposes:
//   customer            : the logged-in user object (null when logged out)
//   token               : raw JWT (also injected into axios via api.js header below)
//   loading             : true on initial /me probe
//   loginWithEmail({email, password})       -> user
//   signupWithEmail({email, password, name, account_type}) -> user
//   sendOtp({mobile, country_code})         -> {ttl_seconds, demo_mode, dev_otp?}
//   verifyOtp({mobile, country_code, otp})  -> user
//   loginWithGoogleSession(sessionId)       -> user
//   logout()
//   refresh()
//
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { syncRecentHotelsWithAccount } from '../lib/recent';

const STORAGE_KEY = 'elite_customer_token';
const CustomerAuthContext = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(!!localStorage.getItem(STORAGE_KEY));

  // Bootstrap: validate stored token via /me on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let alive = true;
    api.get('/customer/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (alive) setCustomer(r.data.user);
        // Pull the account's saved recent-hotels into localStorage so the rail
        // is populated for returning users on new devices/browsers.
        syncRecentHotelsWithAccount();
      })
      .catch(() => {
        if (!alive) return;
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const _store = (data) => {
    if (data?.token) {
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
    }
    if (data?.user) setCustomer(data.user);
    // Best-effort: merge guest recent-hotels history into the account.
    // Fire-and-forget; the rail will refresh via the recent-updated event.
    if (data?.token) syncRecentHotelsWithAccount();
    return data?.user;
  };

  const loginWithEmail = useCallback(async ({ email, password }) => {
    const r = await api.post('/customer/auth/login/email', { email, password });
    return _store(r.data);
  }, []);

  const signupWithEmail = useCallback(async ({ email, password, name, account_type }) => {
    const r = await api.post('/customer/auth/signup/email', { email, password, name, account_type });
    return _store(r.data);
  }, []);

  const sendOtp = useCallback(async ({ mobile, country_code }) => {
    const r = await api.post('/customer/auth/mobile/start', { mobile, country_code });
    return r.data;
  }, []);

  const verifyOtp = useCallback(async ({ mobile, country_code, otp }) => {
    const r = await api.post('/customer/auth/mobile/verify', { mobile, country_code, otp });
    return _store(r.data);
  }, []);

  const loginWithGoogleSession = useCallback(async (payload) => {
    // Accept either the new BYO shape {id_token} or the legacy {session_id}, or
    // a raw string sessionId for older call sites.
    const body = typeof payload === 'string'
      ? { session_id: payload }
      : payload || {};
    const r = await api.post('/customer/auth/google/session', body);
    return _store(r.data);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/customer/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setCustomer(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    const r = await api.get('/customer/auth/me');
    setCustomer(r.data.user);
  }, [token]);

  return (
    <CustomerAuthContext.Provider value={{
      customer, token, loading,
      loginWithEmail, signupWithEmail, sendOtp, verifyOtp, loginWithGoogleSession,
      logout, refresh,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used inside CustomerAuthProvider');
  return ctx;
};
